import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  ShieldCheck,
  Users,
  CreditCard,
  RefreshCw,
  TrendingUp,
  Gift,
  Undo2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function chipTone(status) {
  if (["paid", "captured", "SUCCESS"].includes(status))
    return "text-emerald-300 border-emerald-500/40 bg-emerald-500/5";
  if (["failed", "signature_invalid", "processed"].includes(status))
    return "text-rose-300 border-rose-500/40 bg-rose-500/5";
  if (["pending", "created", "initiated", "partial"].includes(status))
    return "text-amber-300 border-amber-500/40 bg-amber-500/5";
  return "text-slate-300 border-slate-700 bg-slate-900/40";
}

function Chip({ label, tone }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-sm border font-mono text-[9px] uppercase tracking-[0.18em] ${tone}`}
    >
      {label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-4">
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[10px] font-mono text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function GiftPanel({ users, onGifted }) {
  const [email, setEmail] = useState("");
  const [credits, setCredits] = useState(10);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gift-credits", {
        email: email.trim(),
        credits: Number(credits),
        note: note.trim() || undefined,
      });
      toast.success(
        `Gifted ${data.credits_granted} credits to ${data.user_email}. New balance: ${data.new_balance}.`
      );
      setEmail("");
      setNote("");
      setCredits(10);
      onGifted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not gift credits");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-slate-900/60 border border-slate-800 rounded-sm p-5 space-y-3"
      data-testid="gift-panel"
    >
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400">
        <Gift className="h-3 w-3" /> Bulk gift credits
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_2fr_auto] gap-2 items-end">
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            User email
          </Label>
          <Input
            data-testid="gift-email"
            type="email"
            required
            list="admin-user-emails"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@company.com"
            className="mt-1 bg-slate-950 border-slate-800 text-slate-100 rounded-sm focus-visible:ring-cyan-500"
          />
          <datalist id="admin-user-emails">
            {users.map((u) => (
              <option key={u.id} value={u.email}>
                {u.email} — {u.balance} credits
              </option>
            ))}
          </datalist>
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Credits
          </Label>
          <Input
            data-testid="gift-credits"
            type="number"
            required
            min={1}
            max={10000}
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
            className="mt-1 bg-slate-950 border-slate-800 text-slate-100 rounded-sm focus-visible:ring-cyan-500"
          />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Note (optional)
          </Label>
          <Input
            data-testid="gift-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="onboarding, pilot, etc."
            className="mt-1 bg-slate-950 border-slate-800 text-slate-100 rounded-sm focus-visible:ring-cyan-500"
          />
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="gift-submit"
          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm h-10"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Grant"}
        </Button>
      </div>
    </form>
  );
}

function RefundDialog({ tx, open, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy || !tx) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/payments/${tx.order_id}/refund`,
        { reason: reason.trim() || undefined }
      );
      toast.success(
        `Refund initiated · ₹${data.refund_amount / 100} · deducted ${data.credits_deducted} credits`
      );
      onDone?.();
      onOpenChange(false);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 max-w-md rounded-sm"
        data-testid="refund-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Refund payment</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            {tx
              ? `Refund ₹${tx.amount_display} to ${tx.user_email} and deduct up to ${tx.credits} credits.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Reason (optional)
          </Label>
          <Input
            data-testid="refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="customer request / duplicate / …"
            className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm focus-visible:ring-cyan-500"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
          >
            Cancel
          </Button>
          <Button
            data-testid="refund-confirm"
            onClick={submit}
            disabled={busy}
            className="bg-rose-500 hover:bg-rose-400 text-slate-950 font-semibold rounded-sm"
          >
            {busy ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Refunding…
              </>
            ) : (
              "Refund full amount"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);

  const load = async () => {
    setBusy(true);
    setErr(null);
    try {
      const [p, s, u, g] = await Promise.all([
        api.get("/admin/payments"),
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/gift-credits/history"),
      ]);
      setPayments(p.data);
      setStats(s.data);
      setUsers(u.data);
      setGifts(g.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (authLoading) {
    return (
      <div className="text-center py-16 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="text-center py-20" data-testid="admin-denied">
        <ShieldCheck className="h-8 w-8 text-rose-400 mx-auto mb-3" />
        <h2 className="font-display text-2xl font-bold">Admin only</h2>
        <p className="text-sm text-slate-400 mt-2">
          Your account does not have admin privileges.
        </p>
        <Link to="/" className="inline-block mt-6">
          <Button className="bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-sm">
            Back to app
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1 flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Admin
          </div>
          <h1 className="font-display text-3xl font-bold">Payments & credits</h1>
          <p className="text-sm text-slate-400 mt-1">
            Razorpay {stats?.razorpay_mode || "—"} · signed in as {user.email}
          </p>
        </div>
        <Button
          onClick={load}
          disabled={busy}
          size="sm"
          variant="outline"
          className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm gap-1.5"
          data-testid="admin-refresh"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {err && (
        <div className="bg-rose-500/10 border border-rose-500/40 text-rose-300 rounded-sm p-3 text-sm">
          {err}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={CreditCard} label="Total txns" value={stats.total_transactions} />
          <KpiCard
            icon={CreditCard}
            label="Captured"
            value={stats.captured_transactions}
            sub={`${stats.failed_transactions} failed`}
          />
          <KpiCard
            icon={TrendingUp}
            label="Revenue"
            value={`₹${stats.revenue_inr.toLocaleString("en-IN")}`}
            sub="lifetime"
          />
          <KpiCard icon={Users} label="Users" value={stats.total_users} />
        </div>
      )}

      <GiftPanel users={users} onGifted={load} />

      <div className="bg-slate-900/60 border border-slate-800 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 flex items-center justify-between">
          <span>All transactions ({payments.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 bg-slate-950/50">
              <tr>
                <th className="text-left px-4 py-2">User</th>
                <th className="text-left px-4 py-2">Pack</th>
                <th className="text-left px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Payment ID</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => {
                const canRefund = p.payment_status === "paid" && !p.refund_status;
                return (
                  <tr
                    key={p.order_id}
                    data-testid={`admin-row-${p.order_id}`}
                    className="border-t border-slate-800 hover:bg-slate-900/40"
                  >
                    <td className="px-4 py-2 text-slate-100">{p.user_email || "—"}</td>
                    <td className="px-4 py-2">
                      <div>{p.pack_name}</div>
                      <div className="text-[10px] font-mono text-slate-500">
                        {p.credited ? `+${p.credits} credits` : `${p.credits} credits`}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      ₹{p.amount_display}
                      <span className="text-[10px] font-mono text-slate-500 ml-1">
                        {p.currency}
                      </span>
                    </td>
                    <td className="px-4 py-2 space-y-1">
                      <Chip label={p.payment_status} tone={chipTone(p.payment_status)} />
                      {p.refund_status && (
                        <div>
                          <Chip
                            label={`refund ${p.refund_status}`}
                            tone={chipTone(p.refund_status)}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[10px] font-mono text-slate-500">
                      {p.razorpay_payment_id || "—"}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-400">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canRefund && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRefundTarget(p)}
                          data-testid={`refund-${p.order_id}`}
                          className="bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-sm gap-1"
                        >
                          <Undo2 className="h-3 w-3" /> Refund
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {payments.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-500">
                    No transactions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {gifts.length > 0 && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
            Recent credit gifts ({gifts.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 bg-slate-950/50">
                <tr>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-left px-4 py-2">Credits</th>
                  <th className="text-left px-4 py-2">Note</th>
                  <th className="text-left px-4 py-2">By admin</th>
                  <th className="text-left px-4 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {gifts.map((g) => (
                  <tr
                    key={g.id}
                    data-testid={`gift-row-${g.id}`}
                    className="border-t border-slate-800"
                  >
                    <td className="px-4 py-2">{g.user_email}</td>
                    <td className="px-4 py-2 text-cyan-300 font-mono">+{g.credits}</td>
                    <td className="px-4 py-2 text-slate-400">{g.note || "—"}</td>
                    <td className="px-4 py-2 text-slate-400">{g.granted_by_admin}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {new Date(g.granted_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RefundDialog
        tx={refundTarget}
        open={Boolean(refundTarget)}
        onOpenChange={(v) => !v && setRefundTarget(null)}
        onDone={load}
      />
    </div>
  );
}
